import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme, Box, AppBar, Toolbar, IconButton, Avatar, Tooltip,
         Chip, BottomNavigation, BottomNavigationAction, Fab } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import KeyboardShortcutsHelp from '../components/KeyboardShortcutsHelp';
import useDmsPalette from '../hooks/useDmsPalette';
import { useThemeToggle } from '../contexts/ThemeContext';
import { useDms } from '../contexts/DmsContext';
import DmsSidebar from '../components/DmsSidebar';
import DmsBreadcrumbs from '../components/DmsBreadcrumbs';
import OnboardingTour from '../components/OnboardingTour';
import NotificationCenter from '../components/NotificationCenter';

const MAIN_APP_URL = process.env.REACT_APP_MAIN_URL || 'https://soldikeeper.com';

export default function DmsLayout({ user, onLogout }) {
  const MP = useDmsPalette();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleTheme, mode } = useThemeToggle();
  const { selectedVault } = useDms();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [uploadTrigger, setUploadTrigger] = useState(0);

  const shortcuts = [
    { key: 'k', ctrl: true, action: () => navigate('/search'), description: 'Open search' },
    { key: 'u', ctrl: true, action: () => { navigate(`/vault/${selectedVault}`); setUploadTrigger((n) => n + 1); }, description: 'Upload document' },
    { key: '?', action: () => setShortcutsOpen(true), description: 'Show keyboard shortcuts' },
  ];
  useKeyboardShortcuts(shortcuts);

  const bottomNavValue = (() => {
    if (location.pathname === '/') return 0;
    if (location.pathname.startsWith('/vault/')) return 1;
    if (location.pathname.startsWith('/search')) return 3;
    return -1;
  })();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" elevation={0} sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ minHeight: { xs: 56, md: 64 } }}>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 1, display: { xs: 'flex', md: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
            <img src="/assets/images/logo.png" alt="SoldiKeeper" style={{ height: 32 }} />
            <Chip label="DMS" size="small" sx={{ fontWeight: 700, bgcolor: `${MP.green}30`, color: MP.green, fontSize: '0.75rem' }} />
          </Box>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Home Dashboard">
            <IconButton onClick={() => navigate('/')} sx={{ mr: 1, display: { xs: 'none', md: 'flex' } }}>
              <HomeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Search">
            <IconButton onClick={() => navigate('/search')} sx={{ mr: 1, display: { xs: 'none', md: 'flex' } }}>
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            <IconButton onClick={toggleTheme} color="inherit" sx={{ mr: 1 }}>
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Keyboard Shortcuts (?)">
            <IconButton onClick={() => setShortcutsOpen(true)} color="inherit" sx={{ mr: 1, display: { xs: 'none', md: 'flex' } }}>
              <KeyboardIcon />
            </IconButton>
          </Tooltip>
          <NotificationCenter onNavigateToDoc={(docId) => navigate(`/document/${docId}`)} />
          <Tooltip title={`Back to app (${user?.email || ''})`}>
            <Avatar
              onClick={() => window.open(MAIN_APP_URL, '_blank')}
              sx={{ width: 32, height: 32, bgcolor: MP.blue, cursor: 'pointer', fontSize: '0.8rem', mr: 1 }}
            >
              {(user?.name || user?.email || 'U')[0].toUpperCase()}
            </Avatar>
          </Tooltip>
          <Tooltip title="Logout">
            <IconButton onClick={onLogout} size="small">
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <DmsSidebar
        user={user}
        onNavigate={(path) => navigate(path)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        isMobile={isMobile}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: { xs: 7, md: 8 },
          ml: { xs: 0, md: '240px' },
          bgcolor: 'background.default',
          minHeight: '100vh',
          pb: { xs: '72px', md: 0 },
        }}
      >
        <DmsBreadcrumbs onNavigate={(path) => navigate(path)} />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Outlet context={{ uploadTrigger, setUploadTrigger, navigate }} />
        </Box>
      </Box>

      {isMobile && (
        <>
          <Fab
            color="primary"
            size="medium"
            onClick={() => { navigate(`/vault/${selectedVault}`); setUploadTrigger((n) => n + 1); }}
            sx={{
              position: 'fixed', bottom: 40, left: '50%', transform: 'translateX(-50%)',
              zIndex: (t) => t.zIndex.appBar, bgcolor: MP.green, color: '#000',
              width: 52, height: 52, boxShadow: `0 4px 20px ${MP.green}66`,
              '&:hover': { bgcolor: MP.green },
            }}
          >
            <AddIcon />
          </Fab>
          <BottomNavigation
            value={bottomNavValue}
            onChange={(_, val) => {
              if (val === 0) navigate('/');
              else if (val === 1) navigate(`/vault/${selectedVault}`);
              else if (val === 3) navigate('/search');
              else if (val === 4) setMobileOpen(true);
            }}
            sx={{
              position: 'fixed', bottom: 0, left: 0, right: 0,
              zIndex: (t) => t.zIndex.appBar, bgcolor: MP.deepBlue,
              borderTop: `1px solid ${MP.border}`, height: 64,
            }}
          >
            <BottomNavigationAction label="Home" icon={<HomeIcon />} />
            <BottomNavigationAction label="Vaults" icon={<FolderIcon />} />
            <BottomNavigationAction label="" icon={<Box sx={{ width: 52, height: 52 }} />} sx={{ pointerEvents: 'none', opacity: 0 }} />
            <BottomNavigationAction label="Search" icon={<SearchIcon />} />
            <BottomNavigationAction label="More" icon={<MoreHorizIcon />} />
          </BottomNavigation>
        </>
      )}

      <OnboardingTour />
      <KeyboardShortcutsHelp open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} shortcuts={shortcuts} />
    </Box>
  );
}